import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, getPushEnabled, enablePush, disablePush } from "@/lib/push";
import { mt, MBtn, MCard, getGps, fmtT } from "./shared";

const QUICK_RESPONSES = ["Copy", "En Route", "On Scene", "Request Backup", "Unable to Respond",
  "Need Supervisor", "Need EMS", "Need Fire", "Need Police", "Need Tow", "Information Received", "Clear"];

const LINKS = [
  ["Messages", "/comms"], ["BOLOs & Field Ops", "/mdt"],
  ["NCIC / CJIS", "/ncic"], ["Baltimore CFS", "/baltimore"],
  ["Directory", "/directory"], ["Help", "/help"],
];

export function MobileMore({ user, myUnit, onSettings, settings, onView }) {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [devices, setDevices] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [sharing, setSharing] = useState(localStorage.getItem("mob_loc_share") === "1");
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => { getPushEnabled().then(setPushOn).catch(() => {}); }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.success("Push alerts disabled on this device"); }
      else { await enablePush(); setPushOn(true); toast.success("Push alerts ACTIVE — critical alerts reach this device with privacy-safe wording"); }
    } catch (e) { toast.error(e.message || "Push setup failed"); }
    finally { setPushBusy(false); }
  };

  useEffect(() => { client.get("/mobile/devices").then((r) => setDevices(r.data || [])).catch(() => {}); }, []);

  const sendQuick = async (qa) => {
    try {
      const gps = await getGps();
      await client.post("/messages", {
        text: qa || msgText.trim(), scope: "broadcast", channel: "dispatch",
        from_unit: myUnit || null, quick_action: qa || null,
        gps: gps.lat != null ? gps : null,
      });
      toast.success("Message sent to dispatch");
      setMsgText("");
    } catch { toast.error("Send failed"); }
  };

  const toggleShare = async () => {
    const next = !sharing;
    const gps = await getGps();
    try {
      await client.post("/mobile/location-share", { action: next ? "start" : "stop", ...gps });
      localStorage.setItem("mob_loc_share", next ? "1" : "0");
      setSharing(next);
      toast.success(next ? "Operational location sharing STARTED (audited)" : "Location sharing STOPPED (audited)");
    } catch { toast.error("Could not update location sharing"); }
  };

  const toggleDevice = async (d) => {
    try {
      await client.post(`/mobile/devices/${d.device_id}/toggle`, { active: d.active === false });
      const r = await client.get("/mobile/devices");
      setDevices(r.data || []);
    } catch { toast.error("Admin only"); }
  };

  return (
    <div data-testid="mobile-more" className="flex flex-col gap-3 p-3 pb-28">
      {/* Mobile-native tools */}
      <MCard testid="mobile-more-tools">
        <div className="font-head font-bold text-xs mb-2" style={{ color: mt.text }}>FIELD TOOLS</div>
        <div className="grid grid-cols-2 gap-2">
          <MBtn full color={mt.cyan} onClick={() => onView("address")} testid="mobile-more-address">📍 ADDRESS INTEL</MBtn>
          <MBtn full color={mt.purple} onClick={() => onView("reports")} testid="mobile-more-reports">📝 FIELD REPORTS</MBtn>
        </div>
      </MCard>

      {/* Push notifications (§91) */}
      <MCard testid="mobile-more-push">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-head font-bold text-xs" style={{ color: mt.text }}>PUSH ALERTS (LOCK SCREEN)</div>
            <div className="text-[10px] font-mono mt-0.5" style={{ color: pushOn ? mt.green : mt.dim }}>
              {pushSupported()
                ? (pushOn ? "● ACTIVE — generic wording, no sensitive data shown" : "○ OFF — critical alerts only sound in-app")
                : "Not supported — on iPhone, add to Home Screen first"}
            </div>
          </div>
          {pushSupported() && (
            <MBtn small color={pushOn ? mt.red : mt.green} disabled={pushBusy} onClick={togglePush} testid="mobile-push-toggle">
              {pushBusy ? "…" : pushOn ? "DISABLE" : "ENABLE"}
            </MBtn>
          )}
        </div>
      </MCard>

      {/* Quick message to dispatch */}
      <MCard testid="mobile-more-message">
        <div className="font-head font-bold text-xs mb-2" style={{ color: mt.text }}>MESSAGE DISPATCH</div>
        <div className="flex gap-2 mb-2">
          <input data-testid="mobile-msg-input" value={msgText} onChange={(e) => setMsgText(e.target.value)}
            placeholder="Type message…" className="flex-1 px-3 py-2.5 rounded-lg font-mono text-sm"
            style={{ background: mt.surface, border: `1px solid ${mt.border}`, color: mt.text }} />
          <MBtn small color={mt.blue} disabled={!msgText.trim()} onClick={() => sendQuick(null)} testid="mobile-msg-send">SEND</MBtn>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {QUICK_RESPONSES.map((q) => (
            <button key={q} data-testid={`mobile-quick-${q.replace(/\s/g, "-").toLowerCase()}`}
              onClick={() => sendQuick(q)} className="px-2.5 py-1.5 rounded-full text-[11px] font-head font-bold"
              style={{ background: "transparent", color: mt.cyan, border: `1px solid ${mt.cyan}` }}>{q}</button>
          ))}
        </div>
      </MCard>

      {/* Location privacy */}
      <MCard testid="mobile-more-location">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-head font-bold text-xs" style={{ color: mt.text }}>OPERATIONAL LOCATION SHARING</div>
            <div className="text-[10px] font-mono mt-0.5" style={{ color: sharing ? mt.green : mt.dim }}>
              {sharing ? "● ACTIVE — sharing while on duty (audited)" : "○ OFF — start/stop is logged"}
            </div>
          </div>
          <MBtn small color={sharing ? mt.red : mt.green} onClick={toggleShare} testid="mobile-loc-toggle">
            {sharing ? "STOP" : "START"}
          </MBtn>
        </div>
      </MCard>

      {/* Accessibility settings */}
      <MCard testid="mobile-more-settings">
        <div className="font-head font-bold text-xs mb-2" style={{ color: mt.text }}>DISPLAY & ACCESSIBILITY</div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] font-mono" style={{ color: mt.dim }}>Text size:</span>
          {[["1", "A"], ["1.15", "A+"], ["1.3", "A++"]].map(([v, label]) => (
            <button key={v} data-testid={`mobile-textsize-${label}`} onClick={() => onSettings({ ...settings, textScale: v })}
              className="px-3 py-1.5 rounded-lg font-head font-bold text-sm"
              style={settings.textScale === v ? { background: mt.blue, color: "#fff" } : { background: mt.surface, color: mt.dim, border: `1px solid ${mt.border}` }}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono" style={{ color: mt.dim }}>High contrast:</span>
          <button data-testid="mobile-contrast-toggle" onClick={() => onSettings({ ...settings, contrast: !settings.contrast })}
            className="px-3 py-1.5 rounded-lg font-head font-bold text-xs"
            style={settings.contrast ? { background: mt.amber, color: "#000" } : { background: mt.surface, color: mt.dim, border: `1px solid ${mt.border}` }}>
            {settings.contrast ? "ON" : "OFF"}
          </button>
        </div>
      </MCard>

      {/* Links to existing full modules (nothing duplicated) */}
      <MCard testid="mobile-more-links">
        <div className="font-head font-bold text-xs mb-2" style={{ color: mt.text }}>FULL MODULES</div>
        <div className="grid grid-cols-2 gap-1.5">
          {LINKS.map(([label, to]) => (
            <button key={label} data-testid={`mobile-link-${to.replace(/\//g, "")}-${label.split(" ")[0].toLowerCase()}`}
              onClick={() => nav(to)} className="px-3 py-2.5 rounded-lg text-left text-xs font-head font-bold"
              style={{ background: mt.surface, color: mt.text, border: `1px solid ${mt.border}` }}>
              {label} ↗
            </button>
          ))}
        </div>
      </MCard>

      {/* Registered devices */}
      <MCard testid="mobile-more-devices">
        <div className="font-head font-bold text-xs mb-2" style={{ color: mt.text }}>
          REGISTERED DEVICES {user.role === "admin" ? "(ADMIN — all)" : "(yours)"}
        </div>
        {devices.length === 0 && <span className="text-[11px] font-mono" style={{ color: mt.dim }}>None registered yet.</span>}
        {devices.map((d) => (
          <div key={d.device_id} className="flex items-center justify-between py-1.5" style={{ borderBottom: `1px solid ${mt.border}` }}>
            <div className="text-[11px] font-mono" style={{ color: d.active === false ? mt.red : mt.text }}>
              {d.user} · {d.platform || "device"} · seen {fmtT(d.last_seen)}
              {d.active === false && " · DISABLED"}
            </div>
            {user.role === "admin" && (
              <MBtn small outline color={d.active === false ? mt.green : mt.red}
                onClick={() => toggleDevice(d)} testid={`mobile-device-toggle-${d.device_id}`}>
                {d.active === false ? "ENABLE" : "DISABLE"}
              </MBtn>
            )}
          </div>
        ))}
      </MCard>

      <MBtn full outline color={mt.red} onClick={logout} testid="mobile-logout">SECURE LOGOUT</MBtn>
    </div>
  );
}
